import type { CSSProperties } from 'react';

/** Radix-inspired spacing scale keys mapped to `--space-*` tokens. */
export type SpacingScale = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

export interface SpacingProps {
  p?: SpacingScale;
  px?: SpacingScale;
  py?: SpacingScale;
  pt?: SpacingScale;
  pr?: SpacingScale;
  pb?: SpacingScale;
  pl?: SpacingScale;
  m?: SpacingScale;
  mx?: SpacingScale;
  my?: SpacingScale;
  mt?: SpacingScale;
  mr?: SpacingScale;
  mb?: SpacingScale;
  ml?: SpacingScale;
  gap?: SpacingScale;
  gapX?: SpacingScale;
  gapY?: SpacingScale;
}

/** Resolves a spacing scale token to a CSS length. */
export function resolveSpacing(scale: SpacingScale): string {
  return scale === '0' ? '0' : `var(--space-${scale})`;
}

function sideValue(
  explicit: SpacingScale | undefined,
  axis: SpacingScale | undefined,
  all: SpacingScale | undefined,
): string | undefined {
  const scale = explicit ?? axis ?? all;
  return scale === undefined ? undefined : resolveSpacing(scale);
}

/** Builds padding and margin inline styles from spacing props. */
export function buildSpacingStyle(props: SpacingProps): CSSProperties {
  const style: CSSProperties = {};

  const pt = sideValue(props.pt, props.py, props.p);
  const pr = sideValue(props.pr, props.px, props.p);
  const pb = sideValue(props.pb, props.py, props.p);
  const pl = sideValue(props.pl, props.px, props.p);

  if (pt !== undefined) style.paddingTop = pt;
  if (pr !== undefined) style.paddingRight = pr;
  if (pb !== undefined) style.paddingBottom = pb;
  if (pl !== undefined) style.paddingLeft = pl;

  const mt = sideValue(props.mt, props.my, props.m);
  const mr = sideValue(props.mr, props.mx, props.m);
  const mb = sideValue(props.mb, props.my, props.m);
  const ml = sideValue(props.ml, props.mx, props.m);

  if (mt !== undefined) style.marginTop = mt;
  if (mr !== undefined) style.marginRight = mr;
  if (mb !== undefined) style.marginBottom = mb;
  if (ml !== undefined) style.marginLeft = ml;

  if (props.gap !== undefined) {
    style.gap = resolveSpacing(props.gap);
  }
  if (props.gapX !== undefined) {
    style.columnGap = resolveSpacing(props.gapX);
  }
  if (props.gapY !== undefined) {
    style.rowGap = resolveSpacing(props.gapY);
  }

  return style;
}

/** Strips undefined spacing entries for exactOptionalPropertyTypes callers. */
export function pickSpacingProps(props: {
  [K in keyof SpacingProps]?: SpacingScale | undefined;
}): SpacingProps {
  const out: SpacingProps = {};
  const keys = [
    'p',
    'px',
    'py',
    'pt',
    'pr',
    'pb',
    'pl',
    'm',
    'mx',
    'my',
    'mt',
    'mr',
    'mb',
    'ml',
    'gap',
    'gapX',
    'gapY',
  ] as const;

  for (const key of keys) {
    const value = props[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }

  return out;
}
